import React, { useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import { useTranslation } from "react-i18next";
import ScrollReveal from '../components/Animation/ScrollReveal';
import BloomImage from '../assets/bloom.jpg'
import DSC1Image from '../assets/DSC_0059.JPG'
import DSC2Image from '../assets/DSC_0086.JPG'
import ShellfishResearch from "../assets/ShellfishResearch.pdf";
import ScienceDirectResearch from "../assets/ScienceDirect.pdf";

/*
  research.js

  Purpose:
  - Provides a simple Research page that lists published research papers and
    research-related photos. The user can toggle between two tabs (papers/photos).

  Key behaviors:
  - `researchPapers` holds the PDF resources and metadata. Clicking a paper
    expands an embedded PDF viewer (react-pdf-viewer) to read the document
    inline without navigating away.
  - `researchPhotos` is a simple image gallery used in the Photos tab.
  - `activeTab` state controls which tab is visible. `openedPaper` tracks which
    paper (by index) is currently expanded to show the PDF viewer.

  Notes for contributors:
  - Add new papers by appending to the `researchPapers` array and importing the
    PDF in this file (or from a remote URL). Keep `fileUrl` pointing to the file
    import or a public URL.
  - The PDF worker URL is loaded from UNPKG (see <Worker workerUrl=...>).
*/

export default function ResearchPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('papers');
  const [openedPaper, setOpenedPaper] = useState(null); // <- track which paper is opened

  const researchPapers = [
    {
      title: t("research.papers.paper1.title"),
      description: t("research.papers.paper1.description"),
      fileUrl: ShellfishResearch
    },
    {
      title: t("research.papers.paper2.title"),
      description: t("research.papers.paper2.description"),
      fileUrl: ScienceDirectResearch
    },
  ];

  const researchPhotos = [
    BloomImage,
    DSC1Image,
    DSC2Image
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-blue-50">
      <Navbar />

      <div className="px-6 pt-28 max-w-5xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">
          {t("navbar.research")}
        </h1>

        <p className="text-center text-lg text-gray-600 max-w-2xl mx-auto mb-8">
          {t("research.description")}
        </p>

        {/* Tabs */}
        <div className="flex justify-center space-x-4 mb-12 ">
          <button
            onClick={() => setActiveTab('papers')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition transition-transform duration-200 hover:scale-105 ${
              activeTab === 'papers'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-blue-600 border border-blue-600 hover:bg-blue-100'
            }`}
          >
            {t("research.published_papers")}
          </button>
          <button
            onClick={() => setActiveTab('photos')}
            className={`px-6 py-2 rounded-full text-sm font-medium transition transition-transform duration-200 hover:scale-105 ${
              activeTab === 'photos'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-blue-600 border border-blue-600 hover:bg-blue-100'
            }`}
          >
            {t("research.research_photos")}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'papers' && (
          <div className="grid grid-cols-1 gap-10 pb-20">
            {researchPapers.map((paper, index) => (
              <ScrollReveal key={index} delay={index * 60}>
                <div
                  className="bg-white shadow-xl rounded-xl p-8 transition-transform hover:scale-[1.01] cursor-pointer"
                  onClick={() => setOpenedPaper(openedPaper === index ? null : index)} // toggle open/close
                >
                  <h2 className="text-2xl font-semibold text-gray-800 mb-2">{paper.title}</h2>
                  <p className="text-gray-600 mb-4">{paper.description}</p>

                  {openedPaper === index && (
                    <div className="h-[75vh] border border-gray-200 rounded-lg overflow-hidden mt-6">
                      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                        <Viewer fileUrl={paper.fileUrl} />
                      </Worker>
                    </div>
                  )}

                  {openedPaper !== index && (
                    <p className="text-blue-600 text-sm mt-2">{t("research.click_to_view")}</p>
                  )}
                </div>
              </ScrollReveal>
            ))}
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-20">
            {researchPhotos.map((src, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="overflow-hidden rounded-xl shadow-lg">
                  <img
                    src={src}
                    alt={`Research ${index + 1}`}
                    className="w-full h-72 object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </ScrollReveal>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
